import type { TransportConfig } from "./apiTransport";

/**
 * Pure, stateless helpers shared by the prototype API transport — header
 * construction, anonymous-id persistence, JSON body reading, retry
 * classification and the retry backoff timer. Extracted so the transport
 * module stays under its prototype-domain size budget while keeping these
 * utilities co-located and individually testable.
 */

export function buildHeaders(hasJsonBody: boolean, config: TransportConfig): HeadersInit | undefined {
	const headers: Record<string, string> = {
		...buildAnonymousHeader(config),
	};
	if (hasJsonBody) headers["content-type"] = "application/json";
	return Object.keys(headers).length > 0 ? headers : undefined;
}

export function buildAnonymousHeader(config: TransportConfig): Record<string, string> {
	const anonymousId = getAnonymousId(config.anonymousIdStorageKey);
	return anonymousId ? { [config.anonymousIdHeader]: anonymousId } : {};
}

export function getAnonymousId(storageKey: string): string | null {
	try {
		const existing = localStorage.getItem(storageKey);
		if (existing) return existing;
		const next = crypto.randomUUID();
		localStorage.setItem(storageKey, next);
		return next;
	} catch {
		return null;
	}
}

export async function readJsonSafely(response: Response) {
	const text = await response.text();
	if (!text) return null;
	try {
		return JSON.parse(text);
	} catch {
		return text;
	}
}

/**
 * Determines whether a thrown fetch error is transient enough to retry.
 * - AbortError (timeout) → retry (server may be slow, not down)
 * - TypeError (network failure / DNS) → retry (edge blip)
 * - Anything else → don't retry (could be a genuine code error)
 */
export function isTransientNetworkError(error: unknown): boolean {
	if (error instanceof Error) {
		if (error.name === "AbortError") return true;
		// fetch() throws TypeError on network-level failures per the WHATWG spec.
		if (error.name === "TypeError") return true;
	}
	return false;
}

export function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
